// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  ClientListDTO,
  ClientDetailDTO,
  ClientPolicyDTO,
  ClientLOADTO,
  Page,
  ClientListFilters
} from '../types/clientList';

export const clientListApi = {
  /**
   * Get all clients across all profiles with filtering and pagination
   * @param filters - Filter options including search, status, profileId, etc.
   * @param filters.all - When true, fetch all clients across the brokerage (admin only)
   * @param filters.advisorId - Filter by advisor ID (only applicable when all=true)
   */
  getClients: async (filters: ClientListFilters = {}): Promise<Page<ClientListDTO>> => {
    const params: Record<string, string | number | boolean> = {
      page: filters.page ?? 0,
      size: filters.size ?? 20,
    };

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.profileId) {
      params.profileId = filters.profileId;
    }
    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }
    if (filters.sortDirection) {
      params.sortDirection = filters.sortDirection;
    }
    // Admin view: fetch all clients across the brokerage
    if (filters.all) {
      params.all = true;
    }
    // Filter by specific advisor (only applicable in admin view)
    if (filters.advisorId && filters.advisorId !== 'all') {
      params.advisorId = filters.advisorId;
    }

    const response = await api.get<Page<ClientListDTO>>('/clients', {
      params: {
        ...params,
        tagIds: filters.tagIds?.length ? filters.tagIds : undefined,
      },
      // Spring binds List<UUID> from repeated query params (tagIds=a&tagIds=b) —
      // same fix as requestOverviewApi.list / fundsApi.search.
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  /**
   * Get single client details by ID
   */
  getClientDetail: async (clientId: string): Promise<ClientDetailDTO> => {
    const response = await api.get<ClientDetailDTO>(`/clients/${clientId}`);
    return response.data;
  },

  /**
   * Get client's policy relationships
   */
  getClientPolicies: async (clientId: string): Promise<ClientPolicyDTO[]> => {
    const response = await api.get<ClientPolicyDTO[]>(`/clients/${clientId}/policies`);
    return response.data;
  },

  /**
   * Get client's LOAs
   */
  getClientLOAs: async (
    clientId: string,
    page: number = 0,
    size: number = 10
  ): Promise<Page<ClientLOADTO>> => {
    const response = await api.get<Page<ClientLOADTO>>(`/clients/${clientId}/loas`, {
      params: { page, size }
    });
    return response.data;
  },

  /**
   * Remove policy relationship from client
   */
  removePolicyRelationship: async (clientId: string, policyId: string): Promise<void> => {
    await api.delete(`/clients/${clientId}/policies/${policyId}/relationship`);
  },

  /**
   * Download signed LOA PDF. Routed through the unified
   * {@code /api/requests/{requestId}/signed-document} endpoint — fetches
   * the LOA first to resolve its ProfileRequest id, then streams the PDF
   * from the polymorphic ProfileRequest surface.
   */
  downloadLOA: async (loaId: string): Promise<Blob> => {
    const loa = await api.get<{ requestId?: string }>(`/loa/${loaId}`);
    const requestId = loa.data.requestId;
    if (!requestId) {
      throw new Error(
        'LOA has no linked Profile Request — Profile Requests must be enabled for this tenant.',
      );
    }
    const response = await api.get(`/requests/${requestId}/signed-document`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
